import { useState, useCallback, type FormEvent } from 'react'
import { useReqdesk } from './useReqdesk'
import { useReqdeskContext } from './ReqdeskProvider'
import { en } from '../i18n/en'
import { ar } from '../i18n/ar'
import { themeToStyle } from '../theme'
import { saveTrackingToken } from '../storage'
import type { TicketFormProps, TicketResult, WidgetError, SubmitTicketData } from './types'
import { ShadowRoot } from './shadow-root'

const translations: Record<string, Record<string, string>> = { en, ar }

export function TicketForm({ mode = 'inline', onTicketCreated, onError, className, style }: TicketFormProps) {
  const ctx = useReqdeskContext()
  const { submitTicket, isLoading } = useReqdesk()
  const [success, setSuccess] = useState<TicketResult | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const t = useCallback((key: string): string => {
    if (ctx.translations?.[key]) return ctx.translations[key]
    const lang = translations[ctx.language] ?? translations.en
    return lang[key] ?? key
  }, [ctx.language, ctx.translations])

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)

    const title = (formData.get('title') as string)?.trim() ?? ''
    const email = (formData.get('email') as string)?.trim() ?? ''
    const newErrors: Record<string, string> = {}

    if (!title) newErrors.title = t('error.required')
    else if (title.length < 5) newErrors.title = t('error.titleMin')
    if (!email) newErrors.email = t('error.required')
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = t('error.emailInvalid')

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    const data: SubmitTicketData = {
      title,
      description: (formData.get('description') as string)?.trim() || undefined,
      email,
      priority: (formData.get('priority') as SubmitTicketData['priority']) ?? 'medium',
    }

    try {
      const result = await submitTicket(data)
      if (result.trackingToken) saveTrackingToken(ctx.apiKey, result.trackingToken)
      setSuccess(result)
      onTicketCreated?.(result)
    } catch (err) {
      onError?.(err as WidgetError)
    }
  }, [submitTicket, ctx.apiKey, ctx.translations, ctx.language, onTicketCreated, onError, t])

  const cssVars = themeToStyle(ctx.theme)

  const content = success ? (
    <div className="rqd-success" style={{ textAlign: 'center', padding: '24px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>{'\u2705'}</div>
      <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('success.title')}</h3>
      <p>{t('success.ticketNumber')}{success.ticketNumber}</p>
      {success.trackingToken && (
        <>
          <p style={{ fontSize: 13, color: 'var(--rqd-text-secondary)' }}>{t('success.trackingHint')}</p>
          <div className="rqd-token-box">{success.trackingToken}</div>
          <button
            className="rqd-btn rqd-btn-secondary"
            onClick={() => navigator.clipboard.writeText(success.trackingToken!)}
          >
            {t('success.copyToken')}
          </button>
        </>
      )}
    </div>
  ) : (
    <form className="rqd-form" onSubmit={handleSubmit} noValidate>
      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.title')}</label>
        <input className="rqd-input" name="title" placeholder={t('form.titlePlaceholder')} required />
        {errors.title && <div className="rqd-error-text">{errors.title}</div>}
      </div>
      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.description')}</label>
        <textarea className="rqd-textarea" name="description" placeholder={t('form.descriptionPlaceholder')} />
      </div>
      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.email')}</label>
        <input className="rqd-input" name="email" type="email" placeholder={t('form.emailPlaceholder')} required />
        {errors.email && <div className="rqd-error-text">{errors.email}</div>}
      </div>
      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.priority')}</label>
        <select className="rqd-select" name="priority" defaultValue="medium">
          <option value="low">{t('form.priorityLow')}</option>
          <option value="medium">{t('form.priorityMedium')}</option>
          <option value="high">{t('form.priorityHigh')}</option>
          <option value="urgent">{t('form.priorityUrgent')}</option>
        </select>
      </div>
      <button className="rqd-btn rqd-btn-primary" type="submit" disabled={isLoading}>
        {isLoading ? t('form.submitting') : t('form.submit')}
      </button>
    </form>
  )

  if (mode === 'floating') {
    return (
      <ShadowRoot>
        <div className={className} style={{ ...style, ...cssVars } as React.CSSProperties}>
          <div className="rqd-body">{content}</div>
        </div>
      </ShadowRoot>
    )
  }

  return (
    <ShadowRoot>
      <div className={`rqd-inline ${className ?? ''}`} style={cssVars as React.CSSProperties}>
        <div className="rqd-body">{content}</div>
      </div>
    </ShadowRoot>
  )
}
